import {
  GridColDef,
  GridPinnedRowsProp,
  GridRowModel,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { DataGridProProps as MuiDataGridProps } from "@mui/x-data-grid-pro";
import React from "react";

import RoundedBorderContainer from "@/Components/v2/atoms/RoundedBorderContainer/RoundedBorderContainer";
import { DataGrid as MUIDataGrid } from "@/Components/v2/organism/DataGrid";
import { MetricDropdownConfig } from "@/shared/components/DataGrid/ToolBarShared";

/**
 * A simple container component for displaying a data grid
 */
export interface DataGridContainerProps {
  rows: readonly GridRowModel[];
  columns: readonly GridColDef[];
  apiRef: any; // Using any type to avoid compatibility issues between different grid API versions
  toolbarComponent?: React.ReactNode;
  metricDropdown?: MetricDropdownConfig;
  pinnedRows?: GridPinnedRowsProp;
  options?: {
    rowHeight?: number;
    sortModel?: GridSortModel;
    defaultColumnProps?: Partial<GridColDef>;
  };
  muiDataGridProps?: Omit<MuiDataGridProps, "rows" | "columns" | "apiRef">;
}

export const DataGridContainer: React.FC<DataGridContainerProps> = ({
  rows,
  columns,
  apiRef,
  toolbarComponent,
  metricDropdown,
  pinnedRows,
  options = {},
  muiDataGridProps = {},
}) => {
  const {
    rowHeight = 52,
    sortModel = [],
    defaultColumnProps = {
      flex: 1,
      minWidth: 150,
      align: "left",
      headerAlign: "left",
    },
  } = options;

  // Apply default column properties that don't already exist in each column
  const enhancedColumns = columns.map((column) => ({
    ...defaultColumnProps,
    ...column,
  }));

  const hiddenColumns = enhancedColumns.reduce(
    (acc, column) => {
      if (column.hideable) {
        acc[column.field] = false;
      }
      return acc;
    },
    {} as Record<string, boolean>
  );

  return (
    <RoundedBorderContainer>
      <MUIDataGrid
        sx={{ border: "none", position: "relative" }}
        apiRef={apiRef}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableRowSelectionOnClick
        disableMultipleRowSelection
        columnVisibilityModel={hiddenColumns}
        unstable_ignoreValueFormatterDuringExport
        rowHeight={rowHeight}
        rows={rows}
        columns={enhancedColumns}
        pinnedRows={pinnedRows}
        pagination
        autosizeOnMount
        slots={{
          toolbar: () => (
            <>
              {React.isValidElement(toolbarComponent) && metricDropdown
                ? React.cloneElement(
                    toolbarComponent as React.ReactElement,
                    { metricDropdown } as any
                  )
                : toolbarComponent}
            </>
          ),
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
          sorting: {
            sortModel: sortModel,
          },
        }}
        {...muiDataGridProps}
      />
    </RoundedBorderContainer>
  );
};

export default DataGridContainer;
